declare const I18n;
import React, { useMemo } from "react";

type Option = { label: string; value: string };

type Props = {
  label: string;
  value: string;
  isRequired?: boolean;
  options?: string[] | "dynamic:years" | "dynamic:months" | "dynamic:grades" | "dynamic:provinces" | Option[];
  onChange: (value: string) => void;
};

const DropdownInput: React.FC<Props> = ({ label, value, options, onChange, isRequired = false }) => {
  const selectId = useMemo(() => {
    const safeLabel = typeof label === "string" ? label : "dropdown";
    return `onb-select-${safeLabel.replace(/\s+/g, "-").toLowerCase()}-${Math.random().toString(36).slice(2, 8)}`;
  }, [label]);
  let normalizedOptions: Option[] = [];

  if (options === "dynamic:years") {
    const currentYear = new Date().getFullYear();
    normalizedOptions = Array.from({ length: 100 }, (_, i) => {
      const year = String(currentYear - i);
      return { label: year, value: year };
    });
  } else if (options === "dynamic:months") {
    const monthKeys = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];

    normalizedOptions = monthKeys.map((key, index) => ({
      label: I18n.t(`views.date.months.${key}`, { defaultValue: key }),
      value: String(index + 1),
    }));
  } else if (options === "dynamic:provinces") {
    const provinceCodes = [
      "ab", "bc", "mb", "nb", "nl", "nt",
      "ns", "nu", "on", "pei", "qc", "sk", "yk"
    ];
    normalizedOptions = provinceCodes.map((code, index) => ({
      label: I18n.t(`models.common.address.provinces.${code}`),
      value: String(index + 1),
    }));
  } else if (options === "dynamic:grades") {
    // Use translated grade labels
    const gradeKeys = [
      "1", "2", "3", "4", "5", "6", "7", "8", "9",
      "10", "11", "12", "cegep", "post_secondary"
    ];

    normalizedOptions = gradeKeys.map((key) => {
      let translationKey = `models.user.grade_labels.grade`;
      let translatedLabel = I18n.t(translationKey, { grade: key });
      if (key === "cegep") {
        translationKey = `models.user.grade_labels.cegep`;
        translatedLabel = I18n.t(translationKey);
      } else if (key === "post_secondary") {
        translationKey = `models.user.grade_labels.post_secondary`;
        translatedLabel = I18n.t(translationKey);
      }

      return { label: translatedLabel, value: key };
    });

  } else if (Array.isArray(options)) {
    normalizedOptions = options.map((opt: any) => {
      const label =
        opt.labelKey
          ? I18n.t(opt.labelKey, { defaultValue: opt.value || "" })
          : opt.label || opt.value;

      return {
        label,
        value: String(opt.value),
      };
    });
  }

  return (
    <div className="form-group">
      <label htmlFor={selectId} className={`col-form-label ${isRequired ? 'required' : ''}`}>{label}</label>
      <select
        required={isRequired ? true : false}
        id={selectId}
        className="form-control flex-fill"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{I18n.t("common.select")}</option>
        {normalizedOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default DropdownInput;
